/**
 * @module Model.StripePurchaseModel
 *
 * Represents a Stripe purchase with typed purchase items (time slot reservations, program registrations).
 */
import { Model } from "typedefs";
import { ResourceLine, resourceLines } from "app/utils/request/scheduling/facilityResources";

// ============================================================================
// Common Types
// ============================================================================

interface CoachInfo {
  firstName: string;
  lastName: string;
  profilePicture?: string;
  externalId?: string;
}

interface StudentProfileInfo {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture?: string;
  // CN-1323 (CFTB-3292): present for managed dependents so the registration
  // summary shows their age instead of the (dead/temp) email.
  birthdate?: string | null;
  headExternalId?: string | null;
}

/**
 * CN-1323: the managed dependent a session was booked for. Present on a
 * checkout's display info only when a family head booked on behalf of a child;
 * omitted for self-bookings.
 */
export interface CheckoutParticipant {
  externalId?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  profilePicture?: string;
  /** ISO date string (yyyy-mm-dd), used to derive age when the roster misses. */
  birthdate?: string;
}

/**
 * Normalized display info returned by getDisplayInfo() on purchase items.
 * Used by the Checkout component for rendering.
 */
export interface CheckoutDisplayInfo {
  type: 'program' | 'lesson';
  title: string;
  bannerText: string;
  coaches: CoachInfo[];
  facilityName: string;
  location?: string;
  date: string;
  startTime: string;
  endTime: string;
  timeZone: string;
  unitPrice: number;
  currencySymbol: string;
  collectType: 'online' | 'offline' | 'both';
  onlineDefault?: boolean;
  cancelRedirectUrl: string;
  successRedirectUrl: string;
  itemTypeLabel?: string;
  rateLabel?: string;
  // Lesson-specific fields for credit bundle filtering
  lessonRateId?: number;
  coachId?: string;
  facilityId?: string;
  stripeIntegrationEnabled?: boolean;
  // CN-2711: the booking option hides its price, so the summary renders every
  // other detail and drops the rate and total.
  hidePrice?: boolean;
  // CN-1323: the dependent this session is for (only when booking for a child).
  participant?: CheckoutParticipant;
  // The athlete label of the held bay, while the facility has resources on:
  // one "Section: titles" line a section.
  resourceLines?: ResourceLine[];
}

// ============================================================================
// Time Slot Reservation
// ============================================================================

interface TimeSlotDetails {
  lessonRateId: number;
  lessonRateName: string;
  startTime: string;
  endTime: string;
  timeZone: string;
  durationMinutes: number;
  coach: CoachInfo;
  facilityName: string;
  facilityId?: string;
  location?: string;
  collectType: 'online' | 'offline' | 'both';
  onlineDefault?: boolean;
  currencySymbol: string;
  stripeIntegrationEnabled?: boolean;
  hidePrice?: boolean;
}

// ============================================================================
// Program Registration
// ============================================================================

interface ProgramRegistrationDetails {
  programId: number;
  programName: string;
  status: 'pending' | 'completed' | 'cancelled' | 'on_waitlist';
  studentProfile?: StudentProfileInfo;
  startTime: string;
  endTime: string;
  timeZone: string;
  coaches: CoachInfo[];
  facilityName: string;
  location?: string;
  collectType: 'online' | 'offline' | 'both';
  onlineDefault?: boolean;
  currencySymbol: string;
}

// ============================================================================
// Base Purchase Item Model
// ============================================================================

export abstract class PurchaseItemModel {
  id: string;
  type: string;
  priceCents: number;
  description: string;

  constructor(data: any) {
    this.id = data.id;
    this.type = data.type;
    this.priceCents = data.priceCents || data.price_cents;
    this.description = data.description;
  }

  /**
   * Returns normalized display info for the Checkout component.
   */
  abstract getDisplayInfo(): CheckoutDisplayInfo;

  /**
   * Check if this is a time slot reservation
   */
  get isTimeSlotReservation(): boolean {
    return this.type === 'time_slot_reservation' || this.type === 'lesson_schedule';
  }

  /**
   * Check if this is a program registration
   */
  get isProgramRegistration(): boolean {
    return this.type === 'program_registration';
  }
}

// ============================================================================
// Time Slot Reservation Item
// ============================================================================

export class TimeSlotReservationItem extends PurchaseItemModel {
  details: TimeSlotDetails;

  constructor(data: any) {
    super(data);
    this.details = data.details || {};
  }

  getDisplayInfo(): CheckoutDisplayInfo {
    const d = this.details as any; // Allow snake_case access
    const coach = d.coach || {};
    const firstName = coach.firstName || coach.first_name || '';
    const lastName = coach.lastName || coach.last_name || '';
    const coachName = [firstName, lastName].filter(Boolean).join(' ');

    return {
      type: 'lesson',
      title: d.lessonRateName || d.lesson_rate_name || 'Session',
      bannerText: `You are booking a session with ${coachName}`,
      coaches: d.coach ? [{
        firstName,
        lastName,
        profilePicture: coach.profilePicture || coach.profile_picture,
        externalId: coach.externalId || coach.external_id,
      }] : [],
      facilityName: d.facilityName || d.facility_name || '',
      facilityId: d.facilityId || d.facility_id,
      location: d.location,
      date: d.startTime || d.start_time,
      startTime: d.startTime || d.start_time,
      endTime: d.endTime || d.end_time,
      timeZone: d.timeZone || d.time_zone || 'UTC',
      unitPrice: this.priceCents / 100,
      currencySymbol: d.currencySymbol || d.currency_symbol || '$',
      collectType: d.collectType || d.collect_type || 'online',
      onlineDefault: d.onlineDefault ?? d.online_default,
      cancelRedirectUrl: '/sessions/new',
      successRedirectUrl: '/sessions?session_booked_modal=true',
      itemTypeLabel: 'Session',
      rateLabel: 'Session Rate',
      // Lesson-specific fields for credit bundle filtering
      lessonRateId: d.lessonRateId || d.lesson_rate_id,
      coachId: coach.externalId || coach.external_id,
      stripeIntegrationEnabled: d.stripeIntegrationEnabled ?? d.stripe_integration_enabled,
      hidePrice: d.hidePrice ?? d.hide_price ?? false,
      participant: normalizeParticipant(d.participant),
      resourceLines: resourceLines(d),
    };
  }
}

// CN-1323: normalize the participant block (snake_case from the API) into the
// camelCase shape the Checkout view consumes. Returns undefined for
// self-bookings, where the API omits the participant.
function normalizeParticipant(p: any): CheckoutParticipant | undefined {
  if (!p) {
    return undefined;
  }
  return {
    externalId: p.externalId ?? p.external_id,
    firstName: p.firstName ?? p.first_name,
    lastName: p.lastName ?? p.last_name,
    fullName: p.fullName ?? p.full_name,
    profilePicture: p.profilePicture ?? p.profile_picture,
    birthdate: p.birthdate,
  };
}

// ============================================================================
// Program Registration Item
// ============================================================================

export class ProgramRegistrationItem extends PurchaseItemModel {
  details: ProgramRegistrationDetails;

  constructor(data: any) {
    super(data);
    this.details = data.details || {};
  }

  getDisplayInfo(): CheckoutDisplayInfo {
    const d = this.details;

    return {
      type: 'program',
      title: d.programName || 'Program',
      bannerText: 'You are registering for a clinic/event',
      coaches: d.coaches || [],
      facilityName: d.facilityName || '',
      location: d.location,
      date: d.startTime,
      startTime: d.startTime,
      endTime: d.endTime,
      timeZone: d.timeZone || 'UTC',
      unitPrice: this.priceCents / 100,
      currencySymbol: d.currencySymbol || '$',
      collectType: d.collectType || 'online',
      onlineDefault: d.onlineDefault,
      cancelRedirectUrl: '/programs',
      successRedirectUrl: '/programs',
      itemTypeLabel: 'Clinic/Event',
      rateLabel: 'Clinic/Event Rate',
    };
  }

  /**
   * Get registration status
   */
  get registrationStatus(): string | undefined {
    return this.details.status;
  }

  /**
   * Check if registration is active (pending or completed)
   */
  get isActiveRegistration(): boolean {
    return this.details.status === 'pending' || this.details.status === 'completed';
  }

  /**
   * Check if registration is waitlisted
   */
  get isWaitlisted(): boolean {
    return this.details.status === 'on_waitlist';
  }

  /**
   * Get student profile if available
   */
  get studentProfile(): StudentProfileInfo | undefined {
    return this.details.studentProfile;
  }
}

// ============================================================================
// Generic Purchase Item (fallback)
// ============================================================================

class GenericPurchaseItem extends PurchaseItemModel {
  details: any;

  constructor(data: any) {
    super(data);
    this.details = data.details || {};
  }

  getDisplayInfo(): CheckoutDisplayInfo {
    return {
      type: 'program',
      title: this.description || 'Purchase',
      bannerText: 'You are completing a purchase',
      coaches: [],
      facilityName: '',
      location: undefined,
      date: '',
      startTime: '',
      endTime: '',
      timeZone: 'UTC',
      unitPrice: this.priceCents / 100,
      currencySymbol: '$',
      collectType: 'online',
      cancelRedirectUrl: '/',
      successRedirectUrl: '/',
    };
  }
}

// ============================================================================
// Factory Function
// ============================================================================

export function createPurchaseItem(data: any): PurchaseItemModel {
  switch (data.type) {
    case 'time_slot_reservation':
    case 'lesson_schedule':
      return new TimeSlotReservationItem(data);
    case 'program_registration':
      return new ProgramRegistrationItem(data);
    default:
      return new GenericPurchaseItem(data);
  }
}

// ============================================================================
// Type Guards
// ============================================================================

export function isTimeSlotReservation(item: PurchaseItemModel): item is TimeSlotReservationItem {
  return item.type === 'time_slot_reservation' || item.type === 'lesson_schedule';
}

export function isProgramRegistration(item: PurchaseItemModel): item is ProgramRegistrationItem {
  return item.type === 'program_registration';
}

// ============================================================================
// Stripe Purchase Model
// ============================================================================

export class StripePurchaseModel implements Model {
  id: string;
  status: 'pending' | 'paid' | 'refund_pending' | 'refunded' | 'cancelled';
  purchaseType: 'pending_selection' | 'offline' | 'checkout_session' | 'invoice';
  totalCents: number;
  subtotalCents: number;
  currency: string;
  expiresAt: string | null;
  checkoutUrl: string | null;
  purchaseItems: PurchaseItemModel[];
  programIds?: number[];
  stripeIntegrationEnabled?: boolean;
  coachProfileId?: string;
  purchasableType?: string;
  editable?: boolean;
  cancellable?: boolean;

  constructor(purchase: any) {
    this.id = purchase.id;
    this.status = purchase.status;
    this.purchaseType = purchase.purchaseType || purchase.purchase_type || 'pending_selection';
    this.totalCents = purchase.totalCents || purchase.total_cents || 0;
    this.subtotalCents = purchase.subtotalCents || purchase.subtotal_cents || 0;
    this.currency = purchase.currency || 'USD';
    this.expiresAt = purchase.expiresAt || purchase.expires_at || null;
    this.checkoutUrl = purchase.checkoutUrl || purchase.checkout_url || null;
    this.programIds = purchase.programIds || purchase.program_ids;
    this.stripeIntegrationEnabled = purchase.stripeIntegrationEnabled ?? purchase.stripe_integration_enabled;
    this.coachProfileId = purchase.coachProfileId || purchase.coach_profile_id;
    this.purchasableType = purchase.purchasableType || purchase.purchasable_type;
    this.editable = purchase.editable;
    this.cancellable = purchase.cancellable;

    // Parse purchase items using factory (handle both camelCase and snake_case)
    const items = purchase.purchaseItems || purchase.purchase_items || [];
    this.purchaseItems = items.map((item: any) => createPurchaseItem(item));
  }

  /**
   * Get display info from the first purchase item.
   * Used by Checkout component.
   */
  getDisplayInfo(): CheckoutDisplayInfo | undefined {
    if (this.purchaseItems.length === 0) {
      return undefined;
    }
    return this.purchaseItems[0].getDisplayInfo();
  }

  /**
   * Check if the purchase has expired
   */
  get isExpired(): boolean {
    if (!this.expiresAt) {
      return false;
    }
    return new Date(this.expiresAt) < new Date();
  }

  /**
   * Get time remaining until expiration in seconds
   */
  get timeRemainingSeconds(): number {
    if (!this.expiresAt) {
      return 0;
    }
    const now = new Date().getTime();
    const expires = new Date(this.expiresAt).getTime();
    const diff = expires - now;
    return Math.max(0, Math.floor(diff / 1000));
  }

  /**
   * Get only registration items (filter out other purchase item types)
   */
  get registrationItems(): ProgramRegistrationItem[] {
    return this.purchaseItems.filter(isProgramRegistration);
  }

  /**
   * Get active registration items (pending or completed)
   */
  get activeRegistrations(): ProgramRegistrationItem[] {
    return this.registrationItems.filter(item => item.isActiveRegistration);
  }

  /**
   * Get count of active registrations
   */
  get registrationCount(): number {
    return this.activeRegistrations.length;
  }

  /**
   * Get pending registration items (will be registered)
   */
  get pendingItems(): ProgramRegistrationItem[] {
    return this.registrationItems.filter(item => item.details.status === 'pending');
  }

  /**
   * Get waitlisted registration items
   */
  get waitlistedItems(): ProgramRegistrationItem[] {
    return this.registrationItems.filter(item => item.isWaitlisted);
  }

  /**
   * Format total price with currency symbol
   */
  getTotalFormatted(currencySymbol: string = '$'): string {
    const dollars = this.totalCents / 100;
    return `${currencySymbol}${dollars.toFixed(2)}`;
  }

  /**
   * Check if purchase is in a state that allows payment
   */
  get canCheckout(): boolean {
    return this.status === 'pending' &&
           this.purchaseType === 'pending_selection' &&
           !this.isExpired;
  }

  /**
   * Check if purchase is completed
   */
  get isPaid(): boolean {
    return this.status === 'paid';
  }

  /**
   * Check if purchase is awaiting payment (pending selection or in checkout)
   */
  get isPending(): boolean {
    return this.status === 'pending';
  }
}
